// ITAM-2887: the single source of truth for "which persona is this session",
// ported from the dev branch's pharmacistPersona.ts and reshaped for the
// dobrocudan.rs portal. Pure (no store dependency) because the auth thunks
// need it at branch time, where all they have is a raw role array, while the
// selectors need it over Redux state.

/**
 * Staff roles that outrank the advisor role. Meeting any of them suppresses
 * advisor capability entirely: such a token keeps its staff persona and gets
 * no consult surface.
 *
 * DELIVERY_USER is deliberately NOT here: the backend grants it to any
 * consumer account whose delivery order completed, so it is not evidence of
 * a volunteer persona and must not suppress a valid advisor session (Q12).
 * APOTEKA_USER is not here either; every advisor account carries it.
 */
export const STAFF_ABOVE_PORTAL = [
  "ADMIN",
  "MARKETPLACE_ADMIN_USER",
  "PHARMACIST_SUPPORT_USER",
  "PRODAJNO_MESTO_USER",
  "MEDIC_SPECIALIST_USER",
];

/**
 * ASSOCIATION_USER (association administrator) outranks ADVISOR_USER: a
 * dual-role account lands in the association portal and gets no consult
 * surface there (plan Q4), so for the advisor capability it conflicts too.
 */
export const CONFLICTING_STAFF = [...STAFF_ABOVE_PORTAL, "ASSOCIATION_USER"];

export interface AdvisorPersona {
  /** The raw role is present. Never gate a consult surface on this alone. */
  hasAdvisorRole: boolean;
  /** The role is present AND no conflicting staff role outranks it. */
  hasSupportedAdvisorCapability: boolean;
}

export function deriveAdvisorPersona(
  roles: string[] | null | undefined,
): AdvisorPersona {
  const list = roles ?? [];
  const hasAdvisorRole = list.includes("ADVISOR_USER");
  const hasSupportedAdvisorCapability =
    hasAdvisorRole && !list.some((r) => CONFLICTING_STAFF.includes(r));
  return { hasAdvisorRole, hasSupportedAdvisorCapability };
}

export interface AssociationPersona {
  /** The raw role is present. */
  hasAssociationRole: boolean;
  /** The role is present AND no staff role above the portal outranks it. */
  hasSupportedAssociationCapability: boolean;
}

/** Association portal: the administrator persona, same shape as the advisor's. */
export function deriveAssociationPersona(
  roles: string[] | null | undefined,
): AssociationPersona {
  const list = roles ?? [];
  const hasAssociationRole = list.includes("ASSOCIATION_USER");
  const hasSupportedAssociationCapability =
    hasAssociationRole && !list.some((r) => STAFF_ABOVE_PORTAL.includes(r));
  return { hasAssociationRole, hasSupportedAssociationCapability };
}

export type SessionPersonaKind =
  | "ADMIN"
  | "PHARMACIST_SUPPORT"
  | "RETAIL"
  | "ASSOCIATION"
  | "ADVISOR"
  | "DRIVER"
  | "UNSUPPORTED";

/**
 * The precedence lives here alone; the auth thunks only attach side effects
 * per branch. ADVISOR sits above DELIVERY_USER on purpose (Q12): a volunteer
 * who also holds ADVISOR_USER lands in the advisor UI.
 */
export const resolveSessionPersona = (
  roles: string[] | null | undefined,
): SessionPersonaKind => {
  const list = roles ?? [];
  if (list.includes("ADMIN")) return "ADMIN";
  if (list.includes("PHARMACIST_SUPPORT_USER")) return "PHARMACIST_SUPPORT";
  if (list.includes("PRODAJNO_MESTO_USER")) return "RETAIL";
  if (deriveAssociationPersona(list).hasSupportedAssociationCapability) {
    return "ASSOCIATION";
  }
  if (deriveAdvisorPersona(list).hasSupportedAdvisorCapability) {
    return "ADVISOR";
  }
  if (list.includes("DELIVERY_USER")) return "DRIVER";
  return "UNSUPPORTED";
};

/**
 * The persona identity used as a REMOUNT KEY for the consult order manager.
 * It must stay a scalar: interpolating an object yields "42:[object Object]",
 * a key that never changes and silently defeats the teardown.
 */
export type PersonaKey = "association" | "advisor" | "driver" | "other";

export function derivePersonaKey(
  roles: string[] | null | undefined,
): PersonaKey {
  const kind = resolveSessionPersona(roles);
  if (kind === "ASSOCIATION") return "association";
  if (kind === "ADVISOR") return "advisor";
  if (kind === "DRIVER") return "driver";
  return "other";
}

/**
 * The identity a persona-changing token refresh is measured against. A
 * refresh dispatches `loginSuccess`, never `auth/logout`, so a refresh that
 * changes persona would otherwise carry the previous persona's state
 * (including consult caller PII) into the new one. Over-reporting a change
 * is safe (a reset costs a refetch); under-reporting one is the leak.
 */
export const resolvePersonaIdentity = (
  roles: string[] | null | undefined,
): string => `${resolveSessionPersona(roles)}:${derivePersonaKey(roles)}`;
